import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  clearAlerts,
  setErrorAlert,
  setSuccessAlert,
} from "../../../store/alert/alertSlice";
import { stripePaymentThunk } from "../../../store/auth/authThunk";
import Check from "../../../svg/Check";
import { postDataAPI } from "../../../utils/fetchData";
import dizeeLogo from "../../../assets/images/dizee logo.svg";
import TypeWriter from "../../../components/OnBoard/Typewriter";
import styled from "styled-components";
import AccessibilityWidget from "../../../components/AccessibilityWidget";
const Container = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  width: 350px;
  margin: 0 auto;
  padding-left: 35px;
  padding-top: 0;
  overflow: hidden;
  height: 100vh;
  position: relative;
  touch-action: none;
`;

const FormSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-bottom: 80px;
  gap: 2px;
  margin-top: -40px;
  height: 100%;
  position: relative;
  padding-top: 40px;
`;

const ButtonContainer = styled.div`
  height: 40px;
  margin-top: 40px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
`;

const ActionButton = styled.div`
  font-size: 12px;
  color: white;
  cursor: pointer;
  height: 18px;
  line-height: 18px;
  position: relative;
`;

const BillingToggle = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 3px;
  background: rgba(255,255,255,0.05);
  border-radius: 6px;
  width: fit-content;
  position: relative;
  right: 40px;
`;

const ToggleOption = styled.div`
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 10px;
  cursor: pointer;
  background: ${props => props.active ? 'white' : 'transparent'};
  color: ${props => props.active ? 'black' : 'rgba(255,255,255,0.5)'};
  font-weight: ${props => props.active ? '600' : '400'};
  transition: all 0.3s ease;
`;

const Footer = styled.div`
  position: fixed;
  bottom: 27px;
  width: 280px;
`;

const LogoImage = styled.img`
  width: 48px;
  height: auto;
  margin-bottom: 16px;
`;

const FooterText = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  max-width: 280px;
  line-height: 1.5;
`;

const DropdownContainer = styled.div`
  scrollbar-width: none;
  -ms-overflow-style: none;
  
  &::-webkit-scrollbar {
    display: none;
  }
`;

// Updated subscription plans matching backend configuration
const SUBSCRIPTION_PLANS = [
    { 
        label: "Free Plan", 
        value: "free", 
        monthlyPrice: 0, 
        yearlyPrice: 0,
        type: -1, // Free
        tier: "free",
        usageAllowance: 100,
        features: ["100 usage/month", "Basic apps access", "Community support"],
        description: "Get started with essential features at no cost. Perfect for trying out DIZEE."
    },
    { 
        label: "Lite", 
        value: "lite", 
        monthlyPrice: 25, 
        yearlyPrice: 250,
        type: 0, // Monthly
        tier: "lite",
        usageAllowance: 1500,
        features: ["1,500 usage/month", "All apps access", "Basic analytics", "Email support"],
        description: "Essential tools for growing artists and small teams. Includes all apps with basic analytics."
    },
    { 
        label: "Core", 
        value: "core", 
        monthlyPrice: 50, 
        yearlyPrice: 500,
        type: 1, // Monthly
        tier: "core",
        usageAllowance: 4000,
        features: ["4,000 usage/month", "All apps access", "Advanced analytics", "Priority support", "20% efficiency bonus"],
        description: "Advanced features for serious creators. Includes priority support and efficiency bonuses."
    },
    { 
        label: "Max", 
        value: "max", 
        monthlyPrice: 100, 
        yearlyPrice: 1000,
        type: 2, // Monthly
        tier: "max",
        usageAllowance: 10000,
        features: ["10,000 usage/month", "All apps access", "Premium analytics", "White-label tools", "40% efficiency bonus", "Dedicated support"],
        description: "Professional-grade tools for labels and established artists. Includes white-label options and dedicated support."
    }
];

const comment = {
  0: "Choose the usage plan that best fits your needs. Start with essential tools through the Lite plan, or unlock advanced features with Core and Max. All plans include access to every DIZEE app.",
};

const Step3 = ({ setStep, setUser, user }) => {
  const [selectedPlan, setSelectedPlan] = useState(0); // Default to Free Plan
  const [isYearly, setIsYearly] = useState(false); // Default to Monthly
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const userData = useSelector((state) => state.user);
  const dispatch = useDispatch();

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.style.height = '100%';
    document.body.style.touchAction = 'none';
    
    return () => {
      document.body.style.overflow = 'unset';
      document.documentElement.style.overflow = 'unset';
      document.body.style.position = 'static';
      document.body.style.width = 'auto';
      document.body.style.height = 'auto';
      document.body.style.touchAction = 'auto';
    };
  }, []);

  useEffect(() => {
    const fetchApi = async () => {
      try {
        await postDataAPI("stripe/get_payment_history", {
          phone: userData?.phone,
          email: userData?.email,
        }).then((res) => {
          if (res.data.data.response) {
            // Skip domain claiming step and go directly to step 4.5
            setStep(4.5);
          }
        });
      } catch (error) {
        console.log("error", error);
      }
    };
    fetchApi();
  }, [selectedPlan, isYearly]);

  // Set plan in user object based on selection
  useEffect(() => {
    const plan = SUBSCRIPTION_PLANS[selectedPlan];
    if (plan.value === 'free') {
      setUser({ ...user, plan: -1 }); // Free plan
    } else {
      const planType = isYearly ? selectedPlan - 1 + 3 : selectedPlan - 1; // Adjust for free plan at index 0
    setUser({ ...user, plan: planType });
    }
  }, [selectedPlan, isYearly]);

  const handleStripePayment = async () => {
    dispatch(clearAlerts());
    try {
      const plan = SUBSCRIPTION_PLANS[selectedPlan];
      if (plan.value === 'free') {
        // For free plan, skip domain claiming and go directly to step 4.5
        setStep(4.5);
        return;
      }
      
      const planType = isYearly ? selectedPlan - 1 + 3 : selectedPlan - 1; // Adjust for free plan at index 0
      dispatch(stripePaymentThunk({ plan: planType, user: user, email: userData?.email, phone: userData?.phone }));
    } catch (error) {
      dispatch(setErrorAlert("Payment failed. Please try again."));
    }
  };

  const handleNext = () => {
    const plan = SUBSCRIPTION_PLANS[selectedPlan];
    if (plan.value === 'free') {
      // For free plan, skip domain claiming and go directly to step 4.5
      setStep(4.5);
    } else {
      // For paid plans, handle payment
      handleStripePayment();
    }
  };

  const handlePlanSelect = (planIndex) => {
    setSelectedPlan(planIndex);
    setDropdownOpen(false);
  };

  const getCurrentPrice = (plan) => {
    if (plan.value === 'free') return 0;
    return isYearly ? plan.yearlyPrice : plan.monthlyPrice;
  };

  const getSavings = (plan) => {
    if (plan.value === 'free') return { amount: 0, percentage: 0 };
    const monthlyCost = plan.monthlyPrice * 12;
    const savings = monthlyCost - plan.yearlyPrice;
    const percentage = Math.round((savings / monthlyCost) * 100);
    return { amount: savings, percentage };
  };

  const selectedPlanData = SUBSCRIPTION_PLANS[selectedPlan];

  return (
    <Container>
      <FormSection>
        <TypeWriter text={"Select your usage plan"} speed={45} />
        
        {/* Plan Dropdown - Using Dizee Design Language */}
        <div className="relative mt-[20px] w-[280px]">
          <div 
            className="bg-black rounded-[4px] pr-4 py-3 cursor-pointer flex justify-between items-center text-white text-[12px] transition-all duration-200"
            onClick={() => setDropdownOpen(!dropdownOpen)}
          >
            <span>
              {selectedPlanData ? (
                <div className="flex items-center justify-between w-full">
                  <span>{selectedPlanData.label}</span>
                  <span className="ml-4">
                    ${getCurrentPrice(selectedPlanData)}
                    {selectedPlanData.value !== 'free' && `/${isYearly ? 'year' : 'month'}`}
                    {isYearly && selectedPlanData.value !== 'free' && (
                      <span className="text-[#00ff88] text-[10px] ml-2">
                        Save ${getSavings(selectedPlanData).amount}
                      </span>
                    )}
                  </span>
                </div>
              ) : (
                "Select your plan..."
              )}
            </span>
            <div 
              className="w-0 h-0 transition-transform duration-200 ml-2"
              style={{
                borderLeft: '4px solid transparent',
                borderRight: '4px solid transparent',
                borderTop: '4px solid rgba(255, 255, 255, 0.7)',
                transform: dropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)'
              }}
            />
          </div>
          
          {dropdownOpen && (
            <DropdownContainer 
              className="absolute top-full left-0 right-0 bg-[#111111] rounded-[4px] mt-1 z-50 overflow-hidden shadow-lg max-h-[250px] overflow-y-auto"
            >
              {/* Small spacer at top */}
              <div className="h-2 bg-[#111111]"></div>
              
              <div className="px-[5px] flex flex-col gap-y-[10px]">
                {SUBSCRIPTION_PLANS.map((plan, index) => {
                  const price = getCurrentPrice(plan);
                  const savings = getSavings(plan);
                  const isSelected = selectedPlan === index;
                  
                  return (
                                         <div
                       key={plan.value}
                       className={`flex flex-col gap-y-1 cursor-pointer p-3 rounded transition-all duration-200 border ${
                         isSelected
                           ? 'border-[#FF2300]' 
                           : 'border-transparent hover:border-[#FF2300]'
                       }`}
                      onClick={() => handlePlanSelect(index)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-white text-[12px] font-semibold">
                          {plan.label}
                          {isSelected && (
                            <Check className="h-[12px] w-[18px]" />
                          )}
                        </div>
                        <div className="text-white text-[12px] font-semibold">
                          ${price}
                          {plan.value !== 'free' && `/${isYearly ? 'year' : 'month'}`}
                          {isYearly && plan.value !== 'free' && savings.amount > 0 && (
                            <div className="text-[#00ff88] text-[8px]">
                              Save ${savings.amount}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="text-white/70 text-[10px] leading-[1.3] mb-1">
                        {plan.description}
                      </div>
                      <div className="text-white/60 text-[9px]">
                        {plan.features.join(' • ')}
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {/* Small spacer at bottom */}
              <div className="h-2 bg-[#111111]"></div>
            </DropdownContainer>
          )}
        </div>

        <ButtonContainer>
          <ActionButton onClick={handleNext}>
            {selectedPlanData?.value === 'free' ? 'Next' : 'Confirm'}
          </ActionButton>
          
          {/* Monthly/Yearly Toggle - positioned where Skip used to be */}
        <BillingToggle>
          <ToggleOption 
            active={!isYearly} 
            onClick={() => setIsYearly(false)}
          >
            Monthly
          </ToggleOption>
          <ToggleOption 
            active={isYearly} 
            onClick={() => setIsYearly(true)}
          >
              Yearly
          </ToggleOption>
        </BillingToggle>
        </ButtonContainer>
      </FormSection>

      <Footer>
        <LogoImage src={dizeeLogo} alt="Dizee Logo" />
        <FooterText>
          {selectedPlanData ? selectedPlanData.description : comment[0]}
        </FooterText>
      </Footer>
      <AccessibilityWidget />
    </Container>
  );
};

export default Step3;
